import {
  Box,
  Button,
  Divider,
  Flex,
  Show,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import { formatEtherNumber } from "@constants/common";
import { Airdrop, ClaimData } from "@hooks/useClaims";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import React, { useMemo } from "react";

import { useAccount, useSwitchChain } from "wagmi";
import TableSkeleton from "./TableSkeleton";
import GainNudge from "./GainNudge";

const tabs = ["Eligible airdrops", "Ineligible airdrops"];

type ActiveAirDropsProps = {
  airdrops: ClaimData[];
  handleClaim: (data: Airdrop) => void;
  isLoading: boolean;
};

const ActiveAirDrops = ({
  airdrops = [],
  handleClaim,
  isLoading,
}: ActiveAirDropsProps) => {
  const { isConnected, chainId } = useAccount();
  const { switchChain } = useSwitchChain();
  const { openConnectModal } = useConnectModal();

  const handleConnectWallet = () => {
    openConnectModal?.();
  };

  const handleSwitchNetwork = (chainId: number) => () => {
    switchChain({ chainId });
  };

  const [eligibleAirdrops, ineligibleAirdrops] = useMemo(() => {
    const eligibleAirdrops = airdrops.filter(
      (airdrop) => Number(airdrop.data?.availableAmount) > 0
    );
    const ineligibleAirdrops = airdrops.filter(
      (airdrop) =>
        !(Number(airdrop.data?.availableAmount) > 0) &&
        !(Number(airdrop.data?.claimedAmount) > 0)
    );

    return [eligibleAirdrops, ineligibleAirdrops];
  }, [airdrops]);

  const renderTabPanel = (airdrops: ClaimData[], type = "eligible") => {
    if (isLoading) {
      return <TableSkeleton rows={3} columns={3} />;
    }

    if (airdrops.length === 0) {
      return (
        <Box mt={4} padding="1rem 1rem 1rem 0.5rem">
          <Text>No airdrops available.</Text>
        </Box>
      );
    }

    return (
      <Box mt={4}>
        <Table variant="unstyled" color="textPrimary">
          <Thead>
            <Tr>
              <Th pl={1}>Asset</Th>

              <Th>Available amount</Th>

              <Th>Updated on</Th>
              <Th textAlign="right"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {airdrops.map((airdrop: ClaimData, index: number) => {
              const { data, isRefetching } = airdrop;

              return (
                <Tr key={index}>
                  <Td pl={1}>
                    <Flex gap="0.5rem" alignItems="center">
                      <BaseImage
                        src={airdrop.data?.iconPath}
                        height={24}
                        width={24}
                        alt={airdrop.key}
                      />
                      {data.asset}
                    </Flex>
                  </Td>
                  <Td>
                    {type === "eligible"
                      ? `${formatEtherNumber(
                          data.availableAmount.toString()
                        )} ${data.unit}`
                      : "-"}
                  </Td>

                  <Td>{data?.lastUpdatedOn}</Td>

                  <Td textAlign="right">
                    {data.chainId !== chainId && (
                      <Button
                        size="sm"
                        bg="textPrimary"
                        color="white"
                        _hover={{
                          bg: "textPrimary",
                          color: "white",
                        }}
                        onClick={handleSwitchNetwork(data.chainId)}
                        isDisabled={!(data.availableAmount > 0n)}
                      >
                        Switch network
                      </Button>
                    )}
                    {data.chainId === chainId && (
                      <Button
                        size="sm"
                        bg="textPrimary"
                        color="white"
                        _hover={{
                          bg: "textPrimary",
                          color: "white",
                        }}
                        onClick={() => handleClaim(data)}
                        isDisabled={
                          !(data.availableAmount > 0n) || isRefetching
                        }
                      >
                        Claim
                      </Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  };

  const renderMobileTabPanel = (airdrops: ClaimData[], type = "eligible") => {
    if (isLoading) {
      return (
        <Flex padding="0.5rem 1.5rem" pr="0rem">
          <TableSkeleton rows={3} columns={2} />
        </Flex>
      );
    }
    return (
      <Flex direction="column" mb="1rem">
        {airdrops.map((airdrop, index) => {
          const { data, isRefetching } = airdrop;

          return (
            <React.Fragment key={index}>
              <Flex
                key={index}
                padding="1rem"
                backgroundColor="white"
                gap="1rem"
                direction="column"
              >
                <Flex gap="0.5rem" alignItems="center">
                  <BaseImage
                    src={airdrop.data?.iconPath}
                    height={24}
                    width={24}
                    alt={airdrop.key}
                  />
                  <Text fontSize="12px" fontWeight={600} lineHeight="18px">
                    {data?.asset}
                  </Text>
                </Flex>
                {type === "eligible" && (
                  <Flex>
                    <Box>
                      <Text fontSize="12px" color="textPrimary" mb="0.25rem">
                        {`${formatEtherNumber(data.availableAmount.toString())} 
                ${data.unit}`}
                      </Text>
                      <Text fontSize="10px">Available Amount</Text>
                    </Box>
                    <Flex ml="auto" gap="0.5rem">
                      <Box>
                        <Text fontSize="12px" color="textPrimary" mb="0.25rem">
                          {data.lastUpdatedOn}
                        </Text>
                        <Text fontSize="10px">Updated On</Text>
                      </Box>

                      {data.chainId !== chainId && (
                        <Button
                          size="sm"
                          bg="textPrimary"
                          color="white"
                          _hover={{
                            bg: "textPrimary",
                            color: "white",
                          }}
                          onClick={handleSwitchNetwork(data.chainId)}
                          height="auto"
                        >
                          Switch network
                        </Button>
                      )}
                      {data.chainId === chainId && data.availableAmount > 0 && (
                        <Button
                          size="sm"
                          bg="textPrimary"
                          color="white"
                          _hover={{
                            bg: "textPrimary",
                            color: "white",
                          }}
                          onClick={() => handleClaim(data)}
                          height="auto"
                          isDisabled={
                            !(data.availableAmount > 0n) || isRefetching
                          }
                        >
                          Claim
                        </Button>
                      )}
                    </Flex>
                  </Flex>
                )}
              </Flex>
              {index < airdrops.length - 1 && type === "eligible" ? (
                <Divider bg="#AEDFCA" height="1px" />
              ) : null}
            </React.Fragment>
          );
        })}
      </Flex>
    );
  };

  return (
    <Flex direction="column" gap="1rem">
      <Text
        color="textPrimary"
        fontWeight={600}
        fontSize={{ base: "16px", md: "20px" }}
        lineHeight="24px"
      >
        Active airdrops
      </Text>

      {!isConnected ? (
        <Flex
          borderRadius="1.5rem"
          minHeight="13rem"
          backgroundColor="white"
          alignItems="center"
          justifyContent="center"
          gap="1.5rem"
          direction="column"
          padding={{ base: "1rem", md: "2.5rem" }}
        >
          <Text color="#075A5A" fontSize="20px">
            Connect your wallet to view active airdrops
          </Text>
          <Button
            bg="textPrimary"
            color="white"
            _hover={{
              bg: "textPrimary",
            }}
            padding="1rem"
            height="auto"
            borderRadius="6px"
            onClick={handleConnectWallet}
          >
            Connect Wallet
          </Button>
        </Flex>
      ) : (
        <Flex
          padding={{ base: "0", md: "2rem 2.5rem" }}
          backgroundColor="white"
          borderRadius="1.5rem"
          height="fit-content"
          //   minHeight="20rem"
        >
          <Tabs
            variant="soft-rounded"
            width="100%"
            // defaultIndex={
            //   eligibleAirdrops.length > 0 || ineligibleAirdrops.length === 0
            //     ? 0
            //     : 1
            // }
          >
            <TabList
              width="fit-content"
              background="#E4F8F8"
              borderRadius="0.75rem"
              padding="0.25rem"
              gap="0.5rem"
              border="1px solid #B6DFDF"
              margin={{ base: "1rem", md: 0 }}
            >
              {tabs.map((tab) => (
                <Tab
                  _selected={{
                    backgroundColor: "#075A5A",
                    color: "white",
                  }}
                  borderRadius="0.5rem"
                  padding="10px"
                  fontWeight={400}
                  fontSize="14px"
                  lineHeight="16px"
                  key={tab}
                >
                  {tab}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              <TabPanel padding={0}>
                <Show above="md">{renderTabPanel(eligibleAirdrops)}</Show>
                <Show below="md">{renderMobileTabPanel(eligibleAirdrops)}</Show>
              </TabPanel>
              <TabPanel padding={0}>
                <Show above="md">
                  {renderTabPanel(ineligibleAirdrops, "ineligible")}
                </Show>
                <Show below="md">
                  {renderMobileTabPanel(ineligibleAirdrops, "ineligible")}
                </Show>
              </TabPanel>

              <GainNudge />
            </TabPanels>
          </Tabs>
        </Flex>
      )}
    </Flex>
  );
};
export default ActiveAirDrops;
